import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { API_BASE_URL } from '@/lib/constants';
import { useAuthStore } from '@/store/auth.store';

/**
 * /auth/sso/complete — Bootstraps the SPA session after SSO callback.
 *
 * The SSO callback (api.verigrc.com) sets the refresh token in an httpOnly cookie
 * and redirects here. On mount, this page calls the bootstrap endpoint to rotate
 * the refresh token and receive the access token in the JSON body — matching the
 * local-login session-init pattern (D-SM1.4-1).
 */
export function SsoCompletePage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('return_to') ?? '/dashboard';

    async function bootstrap() {
      try {
        // Step 1: rotate refresh token → get access token
        const bootstrapRes = await fetch(`${API_BASE_URL}/auth/sso/bootstrap`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!bootstrapRes.ok) {
          navigate('/auth/sso/error?code=bootstrap_failed', { replace: true });
          return;
        }
        const { data } = await bootstrapRes.json() as { data: { accessToken: string } };

        // Step 2: fetch full user profile with the new access token
        const meRes = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${data.accessToken}` },
          credentials: 'include',
        });
        if (!meRes.ok) {
          navigate('/auth/sso/error?code=bootstrap_failed', { replace: true });
          return;
        }
        const meData = await meRes.json() as { data: unknown };

        // Step 3: initialise auth store — same call path as local login
        login(data.accessToken, meData.data as Parameters<typeof login>[1]);

        // Step 4: navigate to return_to or dashboard
        navigate(returnTo, { replace: true });
      } catch {
        navigate('/auth/sso/error?code=bootstrap_failed', { replace: true });
      }
    }

    bootstrap();
  // Run only on mount — navigate and login refs are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-cyan shadow-lg shadow-accent-cyan/20">
          <ShieldCheck className="h-6 w-6 text-bg-base" />
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Completing sign-in…</span>
        </div>
      </div>
    </div>
  );
}
